import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { OrderLocationForm } from 'sections/LKSection/modules/OrderModule/components/Forms/OrderLocationForm/OrderLocationForm'
import { validateOrderLocation } from 'sections/LKSection/modules/OrderModule/components/Forms/OrderLocationForm/validate'
import { OrdersMap } from 'sections/LKSection/modules/OrderModule/components/OrdersMap/OrdersMap'
import {
  couriersAccessItems,
  entranceItems,
  floorItems,
  OrderKeys,
  pickPointLocationLabels,
} from 'sections/LKSection/modules/OrderModule/data'
import { CouriersAccess, PickPointLocation } from 'sections/LKSection/modules/OrderModule/types'
import { getCreateOrderPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { Padding } from 'components/ui'
import { Info } from 'components/ui/Info/Info'
import { Button, Checkbox, Flex, Form, FormItem, Text } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { sendEvent } from 'utils/helpers/sendEvent'
import { useResize } from 'utils/hooks/useResize'
import { colors } from 'shared/constants/values'
import { Country } from 'shared/types/types'
import { EventType } from 'types/types'

import s from './LocationStep.module.scss'

export type LocationStepType = {
  entrance: PickPointLocation
  floorLevel: PickPointLocation
  couriersAccess: CouriersAccess
}

export const LocationStep = observer(() => {
  const { createOrderUiStore: uiStore, appStore, viewOrderUiStore } = useStore()

  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  const location = uiStore.location

  const { register, handleSubmit, setValue, watch } = useForm<LocationStepType>({
    mode: 'onChange',
    defaultValues: {
      entrance: location.entrance,
      floorLevel: location.floorLevel,
      couriersAccess: location.couriersAccess,
    },
  })

  const { isTablet } = useResize()

  const entrance = watch('entrance')
  const floorLevel = watch('floorLevel')
  const couriersAccess = watch('couriersAccess')

  const isCountryWithShoppingMall = uiStore.companyCountry === Country.RU || uiStore.companyCountry === Country.BY
  const isCouriersAccessVisible = isCountryWithShoppingMall && location.floorLevel === PickPointLocation.shoppingMall
  const isMoving = Boolean(viewOrderUiStore.selectedOrder?.exPickPointId || uiStore.exPickPointId)

  const getKey = (key: string) => `${OrderKeys.locationStep}.${key}`

  const nextStepHandler = async () => {
    if (validateOrderLocation.validate(location, 'boolean', appStore.companyCountry, isMoving)) {
      setLoading(true)
      const res = await uiStore.checkOrderAddress()
      setLoading(false)

      if (res) {
        sendEvent(EventType.orderStep, { step: 2, success: 1 })
        uiStore.setSelectedStep(uiStore.selectedStep + 1)
      }
    } else {
      sendEvent(EventType.orderStep, { step: 2, success: 0, reason: 'Не пройдена валидация' })
    }
  }

  return (
    <Flex className={s.LocationStep} gap={32}>
      <Form onFinish={handleSubmit(nextStepHandler)}>
        <OrderLocationForm uiStore={location} isCreation country={appStore.companyCountry} isMoving={isMoving} />

        <Flex vertical style={{ maxWidth: 600 }}>
          <FormItem label={t(getKey('Этаж'))}>
            <Flex vertical>
              {(isCountryWithShoppingMall ? [...floorItems, PickPointLocation.shoppingMall] : floorItems).map(
                (item) => (
                  <Checkbox
                    {...register('floorLevel')}
                    key={item}
                    checked={item === floorLevel}
                    onChange={() => {
                      setValue('floorLevel', item)
                      location.setFloorLevel(item)
                    }}
                    data-testid="floorLevel"
                  >
                    <Text>{t(getKey(pickPointLocationLabels[item]))}</Text>
                  </Checkbox>
                ),
              )}
            </Flex>
          </FormItem>

          {isCouriersAccessVisible && (
            <FormItem
              label={t(getKey('Доступ в ТЦ для курьеров'))}
              tooltip={
                <Flex vertical gap={4}>
                  <Text style={{ color: colors.white }}>{t(getKey('Только ночной доступ'))}</Text>
                  <Text style={{ color: colors.white }}>{t(getKey('Круглосуточный доступ'))}</Text>
                  <Text style={{ color: colors.white }}>{t(getKey('Только дневной доступ'))}</Text>
                </Flex>
              }
            >
              <Flex vertical>
                {couriersAccessItems.map((item) => (
                  <Checkbox
                    {...register('couriersAccess')}
                    key={item}
                    checked={item === couriersAccess}
                    onChange={() => {
                      setValue('couriersAccess', item)
                      location.setCouriersAccess(item)
                    }}
                    data-testid="couriersAccess"
                  >
                    <Text>{t(getKey(item))}</Text>
                  </Checkbox>
                ))}
              </Flex>
            </FormItem>
          )}

          <Info>
            <Text>{t(getKey('Первая линия это'))}</Text>
          </Info>

          <FormItem label={t(getKey('Расположение входа'))}>
            <Flex vertical>
              {entranceItems.map((item) => (
                <Checkbox
                  {...register('entrance')}
                  key={item}
                  checked={item === entrance}
                  onChange={() => {
                    setValue('entrance', item)
                    location.setEntrance(item)
                  }}
                  data-testid="entrance"
                >
                  <Text>{t(getKey(pickPointLocationLabels[item]))}</Text>
                </Checkbox>
              ))}
            </Flex>
          </FormItem>
        </Flex>

        <Padding pt={16}>
          <Flex gap={8}>
            <Button
              onClick={() => uiStore.setSelectedStep(uiStore.selectedStep - 1)}
              type="default"
              data-testid="previousStepInLocation"
            >
              {t(getCreateOrderPageKey('Предыдущий шаг'))}
            </Button>

            <Button
              onClick={nextStepHandler}
              disabled={location.isSubsidyLoading}
              loading={loading}
              data-testid="nextStepAfterLocation"
            >
              {t(getSharedKey('next'))}
            </Button>
          </Flex>
        </Padding>
      </Form>

      {!isTablet && (
        <OrdersMap
          centerLatitude={location.coordinates.latitude || undefined}
          centerLongitude={location.coordinates.longitude || undefined}
        />
      )}
    </Flex>
  )
})
